<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import relativeTime from 'dayjs/plugin/relativeTime';
	import { createEventDispatcher } from 'svelte';
	import localizedFormat from 'dayjs/plugin/localizedFormat';
	// Stores
	import { adjustmentBetId } from '@/Stores/Store.js';
	import BetAdjustmentStore from '@/Stores/BetAdjustmentStore.js';
	import AuthUserStore from '@/Stores/AuthUserStore.js';
	// Components
	import Modal from '@/Components/Modal.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import TextInput from '@/Components/TextInput.svelte';
	import TextAreaInput from '@/Components/TextAreaInput.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	// Services
	import { notify } from '@/Services/Notify.js';
	import { CurrencyFormat } from '@/Services/Helpers';
	// Constants
	import { AdjustmentType } from '@/Constants/Bet.js';

	dayjs.extend(relativeTime);
	dayjs.extend(localizedFormat);

	export let store;

	const dispatch = createEventDispatcher();

	let sending = false;
	let open = false;
	let amount = '';
	let adjustmentType = AdjustmentType.Credit;
	let note = '';

	$: open = $adjustmentBetId;
	$: bet = open && $adjustmentBetId && store && store.firstWhere('id', $adjustmentBetId);
	$: bet && bet.adjustment_amount && BetAdjustmentStore.getAdjustmentsForBet(bet.id);
	$: creditAdjustmentLimit = $AuthUserStore && getBetCreditAdjustmentLimit();

	//We do this to refresh the current role settings so if the bet credit adjustment limit has changed since last login will refresh it.
	AuthUserStore.getCurrentUser();

	function getBetCreditAdjustmentLimit() {
		let betCreditAdjustmentLimit = false;
		if ($AuthUserStore.is_super_user) {
			return true;
		}
		$AuthUserStore.roles.each((role) => {
			if (
				role &&
				role.role_settings &&
				typeof role.role_settings.bet_credit_adjustment_limit === 'number' &&
				role.role_settings.bet_credit_adjustment_limit > 0
			) {
				if (
					(typeof betCreditAdjustmentLimit === 'number' &&
						role.role_settings.bet_credit_adjustment_limit < betCreditAdjustmentLimit) ||
					typeof betCreditAdjustmentLimit === 'boolean'
				) {
					betCreditAdjustmentLimit = role.role_settings.bet_credit_adjustment_limit;
				}
			}
		});
		if (betCreditAdjustmentLimit === false) {
			adjustmentType = AdjustmentType.Debit;
		}
		return betCreditAdjustmentLimit;
	}

	function confirmClicked() {
		if (!parseFloat(amount) || !note) return;
		let adjusted_amount = adjustmentType === AdjustmentType.Debit ? parseInt(amount * -100) : parseInt(amount * 100);
		if (adjustmentType === AdjustmentType.Credit && typeof creditAdjustmentLimit === 'number') {
			let currentAdjustmentTotal = 0;
			for (const adjustment of $BetAdjustmentStore.sortBy('created_at').all()) {
				if (adjustment.admin_user.id === $AuthUserStore.id) {
					currentAdjustmentTotal = currentAdjustmentTotal + adjustment.amount;
				}
			}
			if (adjusted_amount + currentAdjustmentTotal > creditAdjustmentLimit) {
				notify({
					type: 'danger',
					title: 'Bet Adjustment Failed',
					message: 'That adjustment is above your approved limit',
					timeout: 5
				});
				return;
			}
		} else if (adjustmentType === AdjustmentType.Credit && creditAdjustmentLimit === false) {
			notify({
				type: 'danger',
				title: 'Bet Adjustment Not Possible',
				message: 'Your current role settings do not allow credit adjustments',
				timeout: 5
			});
			return;
		}
		sending = true;
		BetAdjustmentStore.betAdjustment(bet.id, {
			adjusted_amount,
			note
		})
			.then(() => {
				notify({
					type: 'success',
					title: `Bet Ticket #${bet.ticket}`,
					message: `Bet Adjustment of $${CurrencyFormat(adjusted_amount)} Done.`,
					timeout: 3
				});
				dispatch('saved');
				onClose();
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Bet Adjustment Failed',
					message: e.adjusted_amount || e.error[0] || 'Problem adjusting bet',
					timeout: 5
				});
			})
			.finally(() => {
				sending = false;
			});
	}

	function onClose() {
		amount = '';
		note = '';
		adjustmentType = AdjustmentType.Credit;
		BetAdjustmentStore.reset();
		adjustmentBetId.set(false);
	}
</script>

{#if open && bet}
	<Modal on:close={onClose} className="w-1/2 md:max-w-2xl">
		<h2 slot="header" class="pt-2 text-xl font-bold text-center">Bet Adjustment</h2>
		<div class="flex flex-col w-full px-4 pt-2 text-center">
			<p>
				Bet Ticket #{bet.ticket}
				{#if bet.user && bet.user.pin}for PIN: {bet.user && bet.user.pin}{/if}
			</p>
			<p>
				<span class="font-semibold">Status:</span>
				{bet.status.name}
				<span class="ml-2 font-semibold">Win Amount:</span>
				{bet.win_amount ? CurrencyFormat(bet.win_amount) : '-'}
			</p>
			{#if bet.adjustment_amount}
				<p class="pt-1 font-semibold text-red-500">
					Bet Adjustments Total: {CurrencyFormat(bet.adjustment_amount)}
				</p>
			{/if}
			<SelectInput
				label="Bet Adjustment Type"
				className="py-4 w-full"
				name="adjustmentType"
				required={true}
				bind:value={adjustmentType}
			>
				<option value={AdjustmentType.Credit} disabled={creditAdjustmentLimit === false}>Credit</option>
				<option value={AdjustmentType.Debit} selected={creditAdjustmentLimit === false}>Debit</option>
			</SelectInput>
			<TextInput
				name="amount"
				type="number"
				className="pb-4 w-full"
				bind:value={amount}
				required={true}
				placeholder="Bet Adjustment Amount"
			/>
			<TextAreaInput
				name="note"
				className="pb-4 w-full"
				bind:value={note}
				required={true}
				placeholder="Reason for adjustment"
			/>
			<LoadingButton on:click={confirmClicked} loading={sending} className="mb-4 btn btn-primary">Save</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click={onClose}>Cancel</button>
			{#if bet.adjustment_amount}
				<h2 class="mb-1 text-sm font-semibold text-left">Previous Bet Adjustments</h2>
				<div class="grid w-full h-40 grid-cols-5 gap-x-1 mb-4 overflow-y-auto text-xs border adjustment-grid">
					<div class="sticky top-0 py-1 font-semibold bg-grey text-light">Admin</div>
					<div class="sticky top-0 py-1 font-semibold bg-grey text-light">Amount</div>
					<div class="sticky top-0 py-1 font-semibold bg-grey text-light">Note</div>
					<div class="sticky top-0 py-1 font-semibold bg-grey text-light">Tx ID</div>
					<div class="sticky top-0 py-1 font-semibold bg-grey text-light">Date</div>
					{#each $BetAdjustmentStore.sortBy('created_at').all() as adjustment (adjustment.id)}
						<div class="col-start-1 py-2 border-b border-row-separator">
							{adjustment.admin_user.name}
						</div>
						<div class="py-2 border-b border-row-separator">
							{CurrencyFormat(adjustment.amount)}
						</div>
						<div class="py-2 border-b border-row-separator">
							{adjustment.note || ''}
						</div>
						<div class="py-2 border-b border-row-separator">
							{adjustment.transaction_id}
						</div>
						<div class="py-2 border-b border-row-separator">
							{dayjs(adjustment.created_at).format('lll')}
						</div>
					{/each}
				</div>
			{/if}
		</div>
	</Modal>
{/if}

<style>
	.adjustment-grid {
		grid-template-rows: 1.5rem min-content;
	}
</style>
